import type { FeatureCollection, LineString, Point } from "geojson";
import type { StyleSpecification } from "maplibre-gl";

export type OfflinePackSlug = "espace-killy-pilot";

type OfflinePackLineProperties = {
  name: string;
  kind: "run" | "lift" | "ridge";
  difficulty?: "green" | "blue" | "red" | "black";
};

type OfflinePackPointProperties = {
  name: string;
  kind: "base" | "meet";
};

export type OfflinePackMapExtract = {
  source: "packaged-raster-vector-extract";
  tileScheme: "single-raster-tile-plus-inline-geojson";
  bundled: boolean;
  attribution: string;
  style: StyleSpecification;
};

export type OfflinePlannerRoute = {
  id: string;
  startMode: "base" | "mid-mountain";
  title: string;
  startLabel: string;
  durationMinutes: number;
  distanceKm: number;
  verticalM: number;
  legs: {
    time: string;
    label: string;
    detail: string;
    kind: "lift" | "run" | "break" | "traverse";
  }[];
};

export type OfflinePlannerSnapshot = {
  generatedAt: string;
  scoredDate: string;
  observationLabel: string;
  conditions: {
    observation: string;
    snow: string;
    wind: string;
    lifts: string;
    risk: string;
  };
  notes: string[];
  routes: OfflinePlannerRoute[];
};

export type OfflinePackManifest = {
  slug: OfflinePackSlug;
  areaName: string;
  entrySlug: string;
  version: string;
  generatedAt: string;
  sizeBytes: number;
  bbox: [west: number, south: number, east: number, north: number];
  staleAfterHours: number;
  mapExtract: OfflinePackMapExtract;
  plannerSnapshot: OfflinePlannerSnapshot;
  contents: {
    graph: boolean;
    conditions: boolean;
    mapTiles: boolean;
    trailOverlay: boolean;
  };
  demo: {
    plannerHref: string;
    trackerHref: string;
    summary: string;
  };
};

export type InstalledOfflinePackRecord = {
  slug: OfflinePackSlug;
  areaName: string;
  entrySlug: string;
  version: string;
  downloadedAt: string;
  staleAt: string;
  sizeBytes: number;
  bbox: [west: number, south: number, east: number, north: number];
  mapExtract: OfflinePackMapExtract;
  plannerSnapshot: OfflinePlannerSnapshot;
};

export const OFFLINE_PACK_TILE_POLICY = {
  lastVerified: "2026-06-14",
  onlineStyleUrl: "https://tiles.openfreemap.org/styles/liberty",
  cachePublicOpenFreeMapTiles: false,
  offlineMapPath:
    "Use PeakWave-packaged per-area extracts with OpenStreetMap and OpenMapTiles attribution. The validation pack bundles a tiny raster tile plus inline vector overlay for simulator and emulator validation. Scaled production packs should move to PeakWave-hosted PMTiles or MBTiles extracts. Do not bulk-download the public OpenFreeMap tile service for offline packs.",
} as const;

const ESPACE_KILLY_BBOX: [west: number, south: number, east: number, north: number] = [
  6.76,
  45.35,
  7.15,
  45.63,
];

const OFFLINE_TILE_URL =
  "data:image/png;base64," +
  "iVBORw0KGgoAAAANSUhEUgAAAIAAAACACAYAAADDPmHLAAACCklEQVR42u3avU7rQBSF0fP+L0VLQYdECW0kSgpK" +
  "kGgT8mM7zszZq/jaiz17GeU61OHz40e5lUMAwEEAIAAEgAAQAAJAAAgAASAABIAAEAACQAAIAAEgAASAABAAAkAA" +
  "CIBhen59OpvhGwK4NDoQp8+qUkdPhHDqnivxV3wahHP3Wl1uZut/s/PwU34G2GOkLhBueUjK8L0g3HrtZfweCJZe" +
  "b414YyMd/gwQ1lxfjXZjox72iNe1Bc7ypPX4zbTqTeCjb2y2D1wjndXan1+PFJ70aXvUn1d7vGjp/OJl9vcT9eg3" +
  "bh3eus18VrX0Qq69GO/cxz6r2uLmfOs277eUtefNJX/vPupZ1b1v0F/djH1W/ibQn4Q5BAAEgAAQAAJAAAgAAaBu" +
  "fX8djgIgbHAAgscGwOAAJI8NgMEBSB7bfwODxv5vcACCnm4Awp9uAMKfbgCMDYDBATA2AAaPBGDsMAAGDwJg7CAA" +
  "s75kAcDTDYCnGwBPdyoAY4cBMHgQAGOHATB4EABjhwEweF5v7y9/lbH7jntNZfDeA98NgCHmGHgzAIaZb9zFAAzV" +
  "Z+CLAIzWe2AAgscFwMBZAAzaGICxmgMwRnMADrsxAAfZHICDag7AQQQDcAgAOAgABIAAEAACQAAIAAEgAASAABAA" +
  "AkAACAABIAAEgAAQAAJAAAgAASAABIAAEAACQDP0C3CZV686fe3ZAAAAAElFTkSuQmCC";

const ESPACE_KILLY_TRAILS: FeatureCollection<LineString, OfflinePackLineProperties> = {
  type: "FeatureCollection",
  features: [
    {
      type: "Feature",
      geometry: {
        type: "LineString",
        coordinates: [
          [6.977, 45.454],
          [6.962, 45.462],
          [6.949, 45.458],
          [6.934, 45.449],
        ],
      },
      properties: { name: "La Daille cruiser", kind: "run", difficulty: "blue" },
    },
    {
      type: "Feature",
      geometry: {
        type: "LineString",
        coordinates: [
          [7.018, 45.458],
          [7.002, 45.452],
          [6.987, 45.444],
          [6.979, 45.435],
        ],
      },
      properties: { name: "Bellevarde face", kind: "run", difficulty: "black" },
    },
    {
      type: "Feature",
      geometry: {
        type: "LineString",
        coordinates: [
          [6.9, 45.493],
          [6.922, 45.486],
          [6.949, 45.477],
          [6.978, 45.463],
        ],
      },
      properties: { name: "Tignes link", kind: "run", difficulty: "red" },
    },
    {
      type: "Feature",
      geometry: {
        type: "LineString",
        coordinates: [
          [6.846, 45.47],
          [6.872, 45.48],
          [6.9, 45.493],
        ],
      },
      properties: { name: "Lac ridge", kind: "ridge" },
    },
  ],
};

const ESPACE_KILLY_LIFTS: FeatureCollection<LineString, OfflinePackLineProperties> = {
  type: "FeatureCollection",
  features: [
    {
      type: "Feature",
      geometry: {
        type: "LineString",
        coordinates: [
          [6.979, 45.435],
          [7.018, 45.458],
        ],
      },
      properties: { name: "Olympique gondola", kind: "lift" },
    },
    {
      type: "Feature",
      geometry: {
        type: "LineString",
        coordinates: [
          [6.934, 45.449],
          [6.977, 45.454],
        ],
      },
      properties: { name: "La Daille funicular", kind: "lift" },
    },
    {
      type: "Feature",
      geometry: {
        type: "LineString",
        coordinates: [
          [6.846, 45.47],
          [6.9, 45.493],
        ],
      },
      properties: { name: "Toviere lift link", kind: "lift" },
    },
  ],
};

const ESPACE_KILLY_BASES: FeatureCollection<Point, OfflinePackPointProperties> = {
  type: "FeatureCollection",
  features: [
    {
      type: "Feature",
      geometry: { type: "Point", coordinates: [6.979, 45.435] },
      properties: { name: "Val d'Isere", kind: "base" },
    },
    {
      type: "Feature",
      geometry: { type: "Point", coordinates: [6.846, 45.47] },
      properties: { name: "Tignes", kind: "base" },
    },
    {
      type: "Feature",
      geometry: { type: "Point", coordinates: [6.977, 45.454] },
      properties: { name: "Bellevarde", kind: "meet" },
    },
  ],
};

const ESPACE_KILLY_OFFLINE_STYLE: StyleSpecification = {
  version: 8,
  sources: {
    "offline-raster": {
      type: "raster",
      tiles: [OFFLINE_TILE_URL],
      tileSize: 256,
      minzoom: 0,
      maxzoom: 14,
      bounds: ESPACE_KILLY_BBOX,
      attribution:
        "Offline extract packaged by PeakWave. Map data from OpenStreetMap contributors under ODbL. Style schema from OpenMapTiles.",
    },
    "offline-trails": {
      type: "geojson",
      data: ESPACE_KILLY_TRAILS,
    },
    "offline-lifts": {
      type: "geojson",
      data: ESPACE_KILLY_LIFTS,
    },
    "offline-bases": {
      type: "geojson",
      data: ESPACE_KILLY_BASES,
    },
  },
  layers: [
    {
      id: "offline-background",
      type: "background",
      paint: { "background-color": "#e8ebdf" },
    },
    {
      id: "offline-raster",
      type: "raster",
      source: "offline-raster",
      paint: { "raster-opacity": 0.96 },
    },
    {
      id: "offline-lifts-casing",
      type: "line",
      source: "offline-lifts",
      paint: { "line-color": "#ffffff", "line-opacity": 0.8, "line-width": 5 },
      layout: { "line-cap": "round", "line-join": "round" },
    },
    {
      id: "offline-lifts",
      type: "line",
      source: "offline-lifts",
      paint: { "line-color": "#334155", "line-opacity": 0.8, "line-width": 2 },
      layout: { "line-cap": "round", "line-join": "round" },
    },
    {
      id: "offline-ridges",
      type: "line",
      source: "offline-trails",
      filter: ["==", ["get", "kind"], "ridge"],
      paint: { "line-color": "#6b7f5d", "line-opacity": 0.7, "line-width": 2, "line-dasharray": [1.5, 1.5] },
      layout: { "line-cap": "round", "line-join": "round" },
    },
    {
      id: "offline-runs-casing",
      type: "line",
      source: "offline-trails",
      filter: ["==", ["get", "kind"], "run"],
      paint: { "line-color": "#ffffff", "line-opacity": 0.78, "line-width": 7 },
      layout: { "line-cap": "round", "line-join": "round" },
    },
    {
      id: "offline-runs",
      type: "line",
      source: "offline-trails",
      filter: ["==", ["get", "kind"], "run"],
      paint: {
        "line-color": [
          "match",
          ["get", "difficulty"],
          "blue",
          "#2563eb",
          "red",
          "#dc2626",
          "black",
          "#111827",
          "#16a34a",
        ],
        "line-width": 4,
      },
      layout: { "line-cap": "round", "line-join": "round" },
    },
    {
      id: "offline-bases-halo",
      type: "circle",
      source: "offline-bases",
      paint: { "circle-radius": 8, "circle-color": "#ffffff", "circle-opacity": 0.85 },
    },
    {
      id: "offline-bases",
      type: "circle",
      source: "offline-bases",
      paint: {
        "circle-radius": 5,
        "circle-color": ["match", ["get", "kind"], "meet", "#C19A5B", "#16243D"],
        "circle-stroke-color": "#ffffff",
        "circle-stroke-width": 1.5,
      },
    },
  ],
};

const ESPACE_KILLY_PLANNER_SNAPSHOT: OfflinePlannerSnapshot = {
  generatedAt: "2026-06-14T06:00:00.000Z",
  scoredDate: "2026-06-14",
  observationLabel: "Same-day cached pack",
  conditions: {
    observation:
      "Same-day conditions are cached in this pack. Live lift status, weather changes and crowd signals do not update while offline.",
    snow: "Firm early pistes softening on sun-exposed faces by late morning.",
    wind: "Moderate west wind on ridges. Use sheltered alternatives if visibility drops.",
    lifts: "Core Bellevarde, La Daille and Tignes link graph is packaged for the validation pack.",
    risk: "Offline plan is guidance only. Obey local patrol signs, closures and avalanche bulletins.",
  },
  notes: [
    "Preference tweaks and live re-planning need the live planner API.",
    "The pack keeps enough graph, trail and condition data for a same-day fallback inside the downloaded bbox.",
    "Outside the bbox, PeakWave refuses offline mode instead of pretending coverage exists.",
  ],
  routes: [
    {
      id: "base-loop",
      startMode: "base",
      title: "Base start validation loop",
      startLabel: "Val d'Isere base",
      durationMinutes: 155,
      distanceKm: 12.4,
      verticalM: 880,
      legs: [
        {
          time: "09:15",
          label: "Olympique gondola",
          detail: "Use the cached lift graph from Val d'Isere to reach Bellevarde.",
          kind: "lift",
        },
        {
          time: "09:40",
          label: "La Daille cruiser",
          detail: "Warm up on the blue run while the surface is still firm.",
          kind: "run",
        },
        {
          time: "10:20",
          label: "Tignes link traverse",
          detail: "Cross toward Tignes while the ridge wind remains moderate.",
          kind: "traverse",
        },
        {
          time: "11:00",
          label: "Bellevarde face option",
          detail: "Use only if local signs and visibility are good. Fall back to the cruiser if not.",
          kind: "run",
        },
      ],
    },
    {
      id: "mid-mountain-loop",
      startMode: "mid-mountain",
      title: "Mid-mountain fallback loop",
      startLabel: "Bellevarde meeting point",
      durationMinutes: 95,
      distanceKm: 7.1,
      verticalM: 510,
      legs: [
        {
          time: "Now",
          label: "Bellevarde meeting point",
          detail: "Start from the packaged mid-mountain node when GPS lands inside the pack bbox.",
          kind: "break",
        },
        {
          time: "+10 min",
          label: "La Daille cruiser",
          detail: "Drop to the lower cached node and avoid committing to a closed lift.",
          kind: "run",
        },
        {
          time: "+45 min",
          label: "La Daille funicular",
          detail: "Use the cached return lift if local signage confirms it is operating.",
          kind: "lift",
        },
        {
          time: "+70 min",
          label: "Bellevarde ridge lap",
          detail: "Short repeatable loop that stays inside the downloaded extract.",
          kind: "run",
        },
      ],
    },
  ],
};

export const OFFLINE_RESORT_PACKS: OfflinePackManifest[] = [
  {
    slug: "espace-killy-pilot",
    areaName: "Espace Killy",
    entrySlug: "val-disere",
    version: "2026-06-14.1",
    generatedAt: "2026-06-14T06:00:00.000Z",
    sizeBytes: 21_800_000,
    bbox: ESPACE_KILLY_BBOX,
    staleAfterHours: 24,
    mapExtract: {
      source: "packaged-raster-vector-extract",
      tileScheme: "single-raster-tile-plus-inline-geojson",
      bundled: true,
      attribution:
        "Offline extract packaged by PeakWave. Map data from OpenStreetMap contributors under ODbL. Style schema from OpenMapTiles.",
      style: ESPACE_KILLY_OFFLINE_STYLE,
    },
    plannerSnapshot: ESPACE_KILLY_PLANNER_SNAPSHOT,
    contents: {
      graph: true,
      conditions: true,
      mapTiles: true,
      trailOverlay: true,
    },
    demo: {
      plannerHref:
        "/conditions/val-disere/planner?offline-pack=espace-killy-pilot&offline-demo=1&install-demo=1",
      trackerHref: "/companion/track?demo=ski&offline-pack=espace-killy-pilot&install-demo=1",
      summary:
        "Downloadable validation pack with cached graph, bundled map extract, trail overlay and same-day conditions for planner and tracker offline demos.",
    },
  },
];

export function formatOfflinePackSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 MB";
  const mib = bytes / (1024 * 1024);
  if (mib < 10) return `${mib.toFixed(1)} MB`;
  return `${mib.toFixed(1)} MB`;
}

export function createOfflinePackRecord(
  pack: OfflinePackManifest,
  downloadedAtMs: number,
): InstalledOfflinePackRecord {
  const downloadedAt = new Date(downloadedAtMs);
  const staleAt = new Date(downloadedAtMs + pack.staleAfterHours * 60 * 60 * 1000);
  return {
    slug: pack.slug,
    areaName: pack.areaName,
    entrySlug: pack.entrySlug,
    version: pack.version,
    downloadedAt: downloadedAt.toISOString(),
    staleAt: staleAt.toISOString(),
    sizeBytes: pack.sizeBytes,
    bbox: pack.bbox,
    mapExtract: pack.mapExtract,
    plannerSnapshot: pack.plannerSnapshot,
  };
}

export function isOfflinePackStale(record: InstalledOfflinePackRecord, nowMs: number): boolean {
  return Date.parse(record.staleAt) < nowMs;
}

export function packNeedsUpdate(
  pack: OfflinePackManifest,
  record: InstalledOfflinePackRecord | null | undefined,
  nowMs: number,
): boolean {
  if (!record) return true;
  return record.version !== pack.version || isOfflinePackStale(record, nowMs);
}

export function pointInOfflinePack(
  record: Pick<InstalledOfflinePackRecord, "bbox">,
  point: { lat: number; lng: number },
): boolean {
  const [west, south, east, north] = record.bbox;
  return point.lng >= west && point.lng <= east && point.lat >= south && point.lat <= north;
}

export function findOfflinePackForPoint(
  records: InstalledOfflinePackRecord[],
  point: { lat: number; lng: number },
  nowMs: number,
): InstalledOfflinePackRecord | null {
  return records.find((record) => !isOfflinePackStale(record, nowMs) && pointInOfflinePack(record, point)) ?? null;
}

export function offlinePackBySlug(slug: string | null): OfflinePackManifest | null {
  if (!slug) return null;
  return OFFLINE_RESORT_PACKS.find((pack) => pack.slug === slug) ?? null;
}
